import React, { useState, useEffect } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import {
  useCreateQuizMutation,
  useUpdateQuizMutation,
  useFetchQuizByIdMutation,
} from "../../../features/tenant/quiz/quizApi";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { AiOutlineDelete, AiOutlinePlus } from "react-icons/ai";

const AddEditQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isUpdate, setIsUpdate] = useState(location.state ? true : false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [maxAttempts, setMaxAttempts] = useState(1);
  const [pointsPerQuestion, setPointsPerQuestion] = useState(0);
  const [passingMarks, setPassingMarks] = useState(0);
  const [passingMarksRequired, setPassingMarksRequired] = useState(true);
  const [rewardPoints, setRewardPoints] = useState(0);
  const [rewardAvailable, setRewardAvailable] = useState(false);
  
  const [questions, setQuestions] = useState([
    {
      question: "",
      option_a: "",
      option_b: "",
      option_c: "",
      option_d: "",
      correct_option: "A",
    },
  ]);

  const [createQuiz, { isLoading: isCreating }] = useCreateQuizMutation();
  const [updateQuiz, { isLoading: isUpdating }] = useUpdateQuizMutation();
  const [fetchQuizById, { isLoading: isFetchingQuiz }] = useFetchQuizByIdMutation();

  useEffect(() => {
    if (isUpdate && location.state?.id) {
      fetchQuizById({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        id: location.state.id,
      })
        .unwrap()
        .then((res) => {
          const state = res?.body;
          if (state) {
            setTitle(state.title || "");
            setDescription(state.description || "");
            setMaxAttempts(state.max_attempts || 1);
            setPointsPerQuestion(state.points_per_question || 0);
            setPassingMarks(state.passing_marks || 0);
            setPassingMarksRequired(state.passing_marks_required ?? true);
            setRewardPoints(state.reward_points || 0);
            setRewardAvailable(state.reward_available ?? false);
            if (state.questions && state.questions.length > 0) {
              // Deep clone to avoid read-only properties from RTK Query cache
              setQuestions(state.questions.map(q => ({ ...q })));
            }
          }
        })
        .catch((err) => {
          toast.error("Failed to fetch quiz details");
        });
    }
  }, [isUpdate, location.state?.id]);

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        question: "",
        option_a: "",
        option_b: "",
        option_c: "",
        option_d: "",
        correct_option: "A",
      },
    ]);
  };

  const handleRemoveQuestion = (index) => {
    if (questions.length === 1) {
      toast.error("At least one question is required");
      return;
    }
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    // Clone the object at this index to make it mutable
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuestions(newQuestions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !description) {
      toast.error("Title and Description are required");
      return;
    }

    const totalQuestions = questions.length;
    const totalPoints = totalQuestions * pointsPerQuestion;

    const payload = {
      title,
      description,
      total_questions: totalQuestions,
      max_attempts: Number(maxAttempts),
      passing_marks_required: passingMarksRequired,
      passing_marks: passingMarksRequired ? Number(passingMarks) : 0,
      points_per_question: Number(pointsPerQuestion),
      total_points: totalPoints,
      reward_points: Number(rewardPoints),
      reward_available: rewardAvailable,
      questions,
    };

    try {
      let res;
      if (isUpdate) {
        res = await updateQuiz({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
          quiz_id: location.state.id,
          body: payload,
        }).unwrap();
        toast.success("Quiz updated successfully");
      } else {
        res = await createQuiz({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
          body: payload,
        }).unwrap();
        toast.success("Quiz created successfully");
      }
      navigate(-1);
    } catch (error) {
      toast.error(error?.data?.message || "Something went wrong");
    }
  };

  return (
    <div className="flex w-full bg-gray-200 h-full justify-center">
      <div className="w-full">
        <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
          {isUpdate ? "Update" : "Add New"} Quiz
          <LinksComponent />
        </div>
        <div className="p-3">
          <form className="p-4 bg-white shadow rounded-lg mb-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputComponent name="Title" value={title} setValue={setTitle} required={true} />
              <InputComponent name="Max Attempts" type="number" value={maxAttempts} setValue={setMaxAttempts} required={true} />
              <div className="md:col-span-2">
                <InputComponent 
                  name="Description" 
                  type="textarea" 
                  value={description} 
                  setValue={setDescription} 
                  required={true} 
                  rows={3}
                />
              </div>
              <InputComponent name="Points Per Question" type="number" value={pointsPerQuestion} setValue={setPointsPerQuestion} required={true} />
              
              <div className="flex items-center space-x-2 mt-4 ml-2">
                <input
                  type="checkbox"
                  id="passingMarksRequired"
                  checked={passingMarksRequired}
                  onChange={(e) => setPassingMarksRequired(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="passingMarksRequired" className="text-sm font-medium text-gray-700">Passing Marks Required</label>
              </div>

              {passingMarksRequired && (
                <InputComponent name="Passing Marks" type="number" value={passingMarks} setValue={setPassingMarks} required={true} />
              )}

              <div className="flex items-center space-x-2 mt-4 ml-2">
                <input
                  type="checkbox"
                  id="rewardAvailable"
                  checked={rewardAvailable}
                  onChange={(e) => setRewardAvailable(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="rewardAvailable" className="text-sm font-medium text-gray-700">Reward Available</label>
              </div>

              {rewardAvailable && (
                <InputComponent name="Reward Points" type="number" value={rewardPoints} setValue={setRewardPoints} required={true} />
              )}
            </div>

            <div className="mt-8">
              <div className="flex justify-between items-center border-b pb-2 mb-4">
                <h3 className="text-lg font-bold">Questions</h3>
                <button
                  type="button"
                  onClick={handleAddQuestion}
                  className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition"
                >
                  <AiOutlinePlus /> <span>Add Question</span>
                </button>
              </div>

              {questions.map((q, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg mb-4 bg-gray-50 relative">
                  <button
                    type="button"
                    onClick={() => handleRemoveQuestion(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    title="Remove Question"
                  >
                    <AiOutlineDelete size={20} />
                  </button>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Question {index + 1}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={q.question}
                        onChange={(e) => handleQuestionChange(index, "question", e.target.value)}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Option A</label>
                        <input
                          type="text"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                          value={q.option_a}
                          onChange={(e) => handleQuestionChange(index, "option_a", e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Option B</label>
                        <input
                          type="text"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                          value={q.option_b}
                          onChange={(e) => handleQuestionChange(index, "option_b", e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Option C</label>
                        <input
                          type="text"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                          value={q.option_c}
                          onChange={(e) => handleQuestionChange(index, "option_c", e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Option D</label>
                        <input
                          type="text"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                          value={q.option_d}
                          onChange={(e) => handleQuestionChange(index, "option_d", e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Correct Option</label>
                      <select
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                        value={q.correct_option}
                        onChange={(e) => handleQuestionChange(index, "correct_option", e.target.value)}
                        required
                      >
                        <option value="A">Option A</option>
                        <option value="B">Option B</option>
                        <option value="C">Option C</option>
                        <option value="D">Option D</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-6">
              <button
                type="submit"
                disabled={isCreating || isUpdating}
                className="bg-blue-600 text-white px-8 py-2.5 rounded-lg font-bold hover:bg-blue-700 disabled:bg-blue-300 transition shadow-md"
              >
                {isUpdate ? (isUpdating ? "Updating..." : "Update Quiz") : (isCreating ? "Creating..." : "Create Quiz")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEditQuiz;
